import { useNavigate } from 'react-router'
import { orderType } from '../types'
import './SubmissionSuccess.css'
import { Alert, AlertTitle, Button, Link, List, ListItem, Typography } from '@mui/material'

function SubmissionSuccess({
  order,
  uploadError,
  sharepointLink,
}: {
  order: orderType
  uploadError: string[]
  sharepointLink: string[]
}) {
  const navigate = useNavigate()

  return (
    <div className="submission-success">
      <Typography variant="h3">Order Submitted</Typography>
      <SubmitIcon />
      <Typography variant="h6">Your order has been submitted successfully.</Typography>
      <div className="submission-success-container">
        <Typography color="textSecondary">Opportunity Name: {order.Opportunity}</Typography>
        <Typography color="textSecondary">Quote #: {order.QuoteNumber}</Typography>
        <Typography color="textSecondary">Customer PO#: {order.CustomerPO}</Typography>
      </div>
      {uploadError.length > 0 && (
        <Alert severity="error">
          <AlertTitle>
            {sharepointLink.length > 0
              ? 'One or more files failed to upload. Please upload these files directly to SharePoint via the link next to the file name. The link(s) are valid for 30 minutes.'
              : 'One file failed to upload and we were unable to create a SharePoint link. Please send this file to Order Processing directly.'}
          </AlertTitle>
          <List>
            {uploadError.map(file => {
              const filePrefix = file.slice(0, file.search('_'))
              const matchingLink = sharepointLink.find(link => link.startsWith(filePrefix + '_'))
              const url = matchingLink ? matchingLink.slice(matchingLink.search('_') + 1) : ''
              return (
                <ListItem key={file}>
                  {file.slice(file.search('_') + 1)}
                  {matchingLink && (
                    <>
                      {'\u00A0|\u00A0'}
                      <Link href={url} target="_blank" rel="noreferrer">
                        Upload to SharePoint
                      </Link>
                    </>
                  )}
                </ListItem>
              )
            })}
          </List>
        </Alert>
      )}
      <div className="submission-success-container">
        <Button variant="outlined" onClick={() => navigate(`/order/${order.Id}`, { replace: true })}>
          View Order
        </Button>
        <Button variant="contained" onClick={() => navigate('/order')}>
          Return To Home
        </Button>
      </div>
    </div>
  )
}

function SubmitIcon() {
  return (
    <svg width="306" height="305" viewBox="0 0 306 305" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M153 277.054C222.015 277.054 277.963 221.289 277.963 152.499C277.963 83.7086 222.015 27.9434 153 27.9434C83.9843 27.9434 28.0361 83.7086 28.0361 152.499C28.0361 221.289 83.9843 277.054 153 277.054Z"
        fill="#A2BCE0"
      />
      <path
        d="M153 277.97C144.459 277.97 135.924 277.112 127.631 275.42C119.548 273.772 111.598 271.312 104.001 268.109C96.5414 264.964 89.3471 261.072 82.6175 256.541C75.9521 252.052 69.6842 246.898 63.988 241.22C58.2919 235.543 53.1204 229.295 48.6173 222.652C44.0709 215.944 40.1659 208.773 37.011 201.338C33.7976 193.766 31.3296 185.841 29.6757 177.785C27.9786 169.519 27.1182 161.012 27.1182 152.499C27.1182 143.987 27.9786 135.48 29.6757 127.214C31.3296 119.157 33.7976 111.233 37.011 103.661C40.1659 96.2257 44.0709 89.0548 48.6173 82.3473C53.1204 75.7036 58.2919 69.4562 63.988 63.7787C69.6842 58.1012 75.9521 52.9465 82.6175 48.4582C89.3471 43.9266 96.5415 40.0344 104.001 36.8898C111.598 33.6869 119.548 31.227 127.631 29.5784C135.924 27.887 144.459 27.0293 153 27.0293C161.54 27.0293 170.075 27.887 178.368 29.5784C186.451 31.227 194.401 33.6869 201.999 36.8898C209.458 40.0344 216.652 43.9266 223.382 48.4582C230.047 52.9465 236.315 58.1011 242.011 63.7787C247.708 69.4563 252.879 75.7036 257.382 82.3473C261.929 89.0549 265.834 96.2257 268.988 103.661C272.202 111.233 274.67 119.157 276.324 127.214C278.021 135.48 278.881 143.987 278.881 152.499C278.881 161.012 278.021 169.519 276.324 177.785C274.67 185.841 272.202 193.766 268.988 201.338C265.833 208.773 261.929 215.944 257.382 222.652C252.879 229.295 247.708 235.543 242.011 241.22C236.315 246.898 230.047 252.052 223.382 256.541C216.652 261.072 209.458 264.965 201.999 268.109C194.401 271.312 186.451 273.772 178.368 275.42C170.075 277.112 161.54 277.97 153 277.97ZM153 28.8591C84.6007 28.8591 28.9541 84.3239 28.9541 152.499C28.9541 220.675 84.6007 276.14 153 276.14C221.399 276.14 277.045 220.675 277.045 152.499C277.045 84.3239 221.399 28.8591 153 28.8591Z"
        fill="#32578B"
      />
      <path
        d="M152.999 238.165C150.727 238.165 148.591 237.072 147.269 235.223L91.6714 157.507C89.4148 154.352 90.1509 149.972 93.3158 147.723C96.4805 145.473 100.876 146.207 103.132 149.362L152.753 218.723L254.796 63.4262C256.925 60.1853 261.288 59.2786 264.539 61.4013C267.791 63.524 268.701 67.8722 266.571 71.1131L158.887 234.994C157.614 236.932 155.463 238.118 153.139 238.164C153.092 238.165 153.045 238.165 152.999 238.165Z"
        fill="#32578B"
      />
    </svg>
  )
}

export default SubmissionSuccess
